import { ModalForm, TextArea, TextInput, useForm } from '@faceup/form'
import { trpc, useMutation, useQueryClient } from '@faceup/trpc'
import { Button } from '@faceup/ui-base'
import { type AiPromptName, MEDIUM_STRING_MAX_LENGTH, TEXT_MAX_LENGTH } from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useState } from 'react'
import { AiPromptPlaceholderGuide } from './AiPromptPlaceholderGuide'
import { AiPromptPreviewModal } from './AiPromptPreviewModal'
import { aiPromptDefinitions, hasAiPromptPreview } from './aiPromptDefinitions'

const schema = yup.object({
  content: yup.string().trim().required().max(TEXT_MAX_LENGTH),
  note: yup.string().trim().max(MEDIUM_STRING_MAX_LENGTH),
})

type EditAiPromptModalProps = {
  name: AiPromptName
  content: string
  onClose: () => void
}

export const EditAiPromptModal = ({ name, content, onClose }: EditAiPromptModalProps) => {
  const queryClient = useQueryClient()
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const { label } = aiPromptDefinitions[name]

  const { mutateAsync: updatePrompt } = useMutation(trpc.aiPrompt.update.mutationOptions())

  const form = useForm({
    schema,
    values: { content, note: '' },
    afterSubmit: 'nothing',
  })

  return (
    <ModalForm
      title={`Edit - ${label}`}
      open
      onClose={onClose}
      onCancel={onClose}
      width={1000}
      form={form}
      submitButtonText='save'
      additionalContent={
        hasAiPromptPreview(name) && (
          <Button onClick={() => setPreviewContent(form.getValues('content'))}>
            Preview & test
          </Button>
        )
      }
      onSubmit={async values => {
        await updatePrompt({
          name,
          content: values.content,
          note: values.note,
          shouldSynchronizeAllRegions: true,
        })
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: trpc.aiPrompt.list.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.aiPrompt.history.queryKey({ name }) }),
        ])
        onClose()
        return true
      }}
    >
      <AiPromptPlaceholderGuide name={name} />
      <TextArea
        control={form.control}
        name='content'
        label='Prompt'
        rows={25}
        className='font-mono'
      />
      <TextInput
        control={form.control}
        name='note'
        label='Note'
        placeholder='What changed and why (optional)'
      />
      {hasAiPromptPreview(name) && (
        <AiPromptPreviewModal
          name={name}
          content={previewContent}
          title='Preview & test'
          onClose={() => setPreviewContent(null)}
        />
      )}
    </ModalForm>
  )
}
